import type { ThemeConfig } from 'antd';

/**
 * The bridge between the stylesheet and Ant Design.
 *
 * **This file is no longer where restyling happens.** The visual language — palette, typography,
 * radii, the floating sidebar and header, every component class — lives in `style/css/style.css`,
 * vendored from the Dreams ERP template. What is left here is the subset Ant Design needs to be
 * told in JavaScript, because a component that paints its own surfaces cannot read a Tailwind
 * class: the Table on every list screen, and the Select, DatePicker and Modal that go with it.
 *
 * Every value below is a copy of a `@theme` token in that stylesheet, and the comment names which
 * one. They are duplicated rather than read at runtime because `ThemeConfig` is consumed during
 * render, before `getComputedStyle` on `:root` would return anything useful during SSR or the
 * first paint. **If a value here disagrees with style.css, style.css is right.**
 *
 * Two things survive from the original direction because they are operational rather than
 * decorative:
 *
 * - Document numbers, quantities and money stay in tabular mono. Scanning a column of challan
 *   numbers is the single most common thing anyone does here, and a proportional face makes a
 *   column of amounts impossible to read.
 * - The financial year keeps the one warm accent in the product (`amber`). Posting a challan to the
 *   wrong year is expensive to unpick, so that marker is allowed to shout and nothing else is.
 */

/**
 * The handful of colours JavaScript still needs, each mirroring a `@theme` token.
 *
 * Deliberately shorter than it was. Anything a component can express as a Tailwind class —
 * `text-primary`, `border-border-color`, `bg-light` — is not here, because the moment a colour is
 * available in two places one of them goes stale.
 */
export const palette = {
  /** `--color-gray-900`. Headings and primary text. */
  ink: '#2F3A4B',
  /** `--color-default`. Body copy, labels, secondary text. */
  graphite: '#67707D',
  /**
   * `--color-dark`. Buttons, active navigation, anything the user is meant to press. Near-black
   * slate rather than the brand colour: it keeps `teal` meaning "this is us / this is state"
   * instead of "click me".
   */
  slate: '#1E293B',
  /** `--color-primary`. Brand — wordmark, links, selection, positive state. Not for buttons. */
  teal: '#0F766E',
  /** `--color-primary-transparent`. Selected rows, soft badges. */
  tealWash: '#ECF5F1',
  /** `--color-success`. */
  green: '#059669',
  /**
   * `--color-warning`. The single warm accent: financial year marker and warnings, nothing else.
   *
   * Darker than the template's `#D97706`, which was 3.19:1 on white and 2.91:1 on its own wash --
   * below AA everywhere amber appeared, and it appears as small text in 23 places. The dark theme
   * keeps the original and pins it; see the amber block at the end of `style.css`.
   */
  amber: '#A95D05',
  /** `--color-danger`. */
  red: '#B91C1C',
  /** `--color-light`. Page background. */
  canvas: '#F8F9FA',
  /** `--color-white`. Cards, tables, the sidebar and the header. */
  surface: '#FFFFFF',
  /** `--color-border-color`. Every border in the product, at 1px. */
  rule: '#E8E9EC',
} as const;

/**
 * `--font-family-primary`, with the fallbacks the template does not carry.
 *
 * Geist is self-hosted through `@fontsource-variable/geist` (imported in `index.css`) rather than
 * fetched from a CDN — an ERP that stops looking like itself when the shop's internet drops is not
 * acceptable, and it keeps the app free of third-party requests. Noto Sans Gujarati stays in the
 * stack so Phase 6 is a content change rather than a typographic one (spec 24.2).
 */
export const fontSans =
  "'Geist Variable', Geist, 'Segoe UI Variable Text', 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Noto Sans Gujarati', 'Noto Sans', Roboto, sans-serif";

/**
 * Tabular mono for document numbers, quantities and money.
 *
 * Functional, not stylistic: `CH-2025-26-0004` above `CH-2025-26-0011` only reads as a sequence
 * when the digits line up. Available as the `font-mono-nums` class in style.css for markup;
 * exported here for the antd components that take a style object.
 */
export const fontMono =
  "'Geist Mono Variable', 'Geist Mono', 'Cascadia Mono', 'Consolas', ui-monospace, 'SF Mono', monospace";

/** `--radius-md`. The one radius — every card, control, input and badge. */
export const radius = 6;

export const theme: ThemeConfig = {
  token: {
    // The action colour is slate, not the brand. See `palette.slate`.
    colorPrimary: palette.slate,
    colorLink: palette.teal,
    colorInfo: palette.teal,
    colorSuccess: palette.green,
    colorWarning: palette.amber,
    colorError: palette.red,

    colorTextBase: palette.ink,
    colorText: palette.ink,
    colorTextSecondary: palette.graphite,
    colorTextTertiary: palette.graphite,
    colorBgLayout: palette.canvas,
    colorBgContainer: palette.surface,
    colorBorder: palette.rule,
    colorBorderSecondary: palette.rule,

    /*
     * Selected-row surfaces, set explicitly rather than derived.
     *
     * Ant Design derives `controlItemBgActive` from `colorPrimary`, and that derivation assumes a
     * saturated mid-tone primary. Ours is a near-black slate, which it turns into a muddy
     * `rgb(115,119,122)` — a dark bar with dark text on it. Every selected-item surface shares this
     * token (dropdowns, Select options, date-picker cells), so it is fixed here once.
     */
    controlItemBgActive: palette.tealWash,
    controlItemBgActiveHover: '#DFEBE6',
    controlItemBgHover: palette.canvas,

    fontFamily: fontSans,
    fontFamilyCode: fontMono,

    /*
      `fontSize` and `controlHeight` are deliberately absent.

      They were 13 and 34 — chosen for density, on the argument that every extra pixel is a row that
      no longer fits on a counter terminal. That argument was wrong for the room this runs in: the
      type was unreadable for some customers and 34px missed the 44px touch target a counter tablet
      is built around. They are now a setting, resolved per person and then per company by
      `shell/textScale.ts` and supplied by `AppProviders`. The old pair survives there exactly, as
      `compact`.
    */

    borderRadius: radius,
    borderRadiusLG: radius,
    borderRadiusSM: 4,

    // `--shadow-sm` in the template. Barely there — it separates layers without casting a shadow.
    boxShadow: '0 4px 4px 0 rgba(219, 219, 219, 0.25)',
    boxShadowSecondary: '0 4px 4px 0 rgba(219, 219, 219, 0.25)',

    wireframe: false,
  },

  components: {
    /*
     * Table is the reason this file still exists. The template styles its own `<table>` markup and
     * reaches for antd only for the datatable, so these tokens are what make the two agree — with
     * the few surfaces antd paints in CSS rather than tokens handled in `index.css`.
     */
    Table: {
      cellPaddingBlock: 12,
      cellPaddingInline: 12,
      headerBg: palette.canvas,
      headerColor: palette.ink,
      headerSplitColor: 'transparent',
      borderColor: palette.rule,
      rowHoverBg: palette.canvas,
      rowSelectedBg: palette.tealWash,
      rowSelectedHoverBg: palette.tealWash,
      footerBg: palette.surface,
    },
    Input: {
      paddingBlock: 6,
      // No `colorBgContainer` here. It only restated the global token, and a component-level token
      // *wins* over the one the dark algorithm derives — so restating it pinned every input white
      // on a dark page. A component entry that merely repeats a global is never harmless.
      activeShadow: 'none',
    },
    Select: { optionSelectedBg: palette.tealWash },
    DatePicker: { cellActiveWithRangeBg: palette.tealWash },
    Modal: { titleFontSize: 16, headerBg: palette.surface },
    Tooltip: { colorBgSpotlight: palette.slate, borderRadius: 4 },
    Button: {
      primaryShadow: 'none',
      defaultShadow: 'none',
      dangerShadow: 'none',
      fontWeight: 500,
    },
  },
};
